import Decimal from "decimal.js";
import { Product, ProductComponent } from "../../types";
import { getDiscountPrice } from "../shared/getDiscountPrice";
import { getMarginPrice } from "../shared/getMarginPrice";
import { findProductComponent } from "../productComponents/findProductComponent";

const RAW_OVERHEAD_CODE = "Raw Overhead";

function getComponentRawOverheadContribution(
  component: ProductComponent,
  components: ProductComponent[],
  processedIds: Set<string> = new Set(),
): number {
  if (processedIds.has(component.id)) {
    throw new Error(`Circular dependency detected for component ${component.id}`);
  }

  const branchProcessedIds = new Set(processedIds);
  branchProcessedIds.add(component.id);

  const componentContribution = component.code === RAW_OVERHEAD_CODE
    ? new Decimal(
        getMarginPrice(
          getDiscountPrice(component.price, component.discount),
          component.margin,
        ),
      )
    : new Decimal(0);

  return component.components.reduce((total, childLink) => {
    const child = findProductComponent(components, childLink.id);
    if (!child) return total;

    return total.add(
      new Decimal(
        getComponentRawOverheadContribution(
          child,
          components,
          branchProcessedIds,
        ),
      ).mul(childLink.qty),
    );
  }, componentContribution).toNumber();
}

export function getRawOverheadContribution(
  product: Product,
  components: ProductComponent[],
): number {
  const contribution = product.components.reduce((total, componentLink) => {
    const component = findProductComponent(components, componentLink.id);
    if (!component) return total;

    let componentContribution = new Decimal(
      getComponentRawOverheadContribution(component, components),
    ).mul(componentLink.qty);

    if (componentLink.margin) {
      componentContribution = componentContribution.div(
        new Decimal(1).sub(new Decimal(componentLink.margin).div(100)),
      );
    }

    return total.add(componentContribution);
  }, new Decimal(0));

  return Number(contribution.toFixed(2));
}
